// Facility acta "Resultado de la visita" — the closed acta's checklist
// outcome as the PDF and the public share read it (ADR-0026 score band,
// ADR-0137 per-item rows, ADR-0171).
//
// Pure. The input is the same ChecklistItemStateRow the asset-state
// derivations consume, so the caller decides the source (a terminal
// report's checklist_snapshot, or the live rows ensureMutable already
// freezes) and this module never knows which. Only the supervisor's
// verdict is read — /close and /sign require one on every item
// (ensureFacilitySectionsReadyToSign), so on a terminal acta it is the
// same verdict the PWA's `supervisor ?? ai` shows.
//
// The output is deliberately narrower than the row: no ids, no catalog
// keys, no photos, no notes, no AI proposal. Severity and description
// only ride on the items that materialize as observations (con_obs /
// no_cumple) — the same set whose descriptions already cross the public
// boundary as findings — so the conforming items add a label and a
// verdict, nothing else.
import { z } from "zod";
export const actaChecklistItemSchema = z
    .object({
    label: z.string(),
    // NULL = not reviewed («—»). Unreachable on a closed acta today, kept
    // so a pre-guard historical row still renders instead of vanishing.
    verdict: z.enum(["cumple", "con_obs", "no_cumple", "n_a"]).nullable(),
    // Recipe severity key; only on con_obs / no_cumple.
    severity: z.string().nullable(),
    // human ?? ai description; only on con_obs / no_cumple.
    description: z.string().nullable(),
})
    .strict();
export const actaChecklistSectionSchema = z
    .object({
    label: z.string(),
    // Same counting rule as the closed-acta screen
    // (FacilityActaCerrada.blockCountsFor): `total` is every item of the
    // section, N/A and unreviewed included — the «X/Y cumplen» line.
    counts: z
        .object({
        cumple: z.number().int().nonnegative(),
        conObs: z.number().int().nonnegative(),
        noCumple: z.number().int().nonnegative(),
        nA: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    })
        .strict(),
    items: z.array(actaChecklistItemSchema),
})
    .strict();
export const actaChecklistSchema = z
    .object({
    // Items across the whole acta — the «En base a N ítems revisados en
    // M secciones» line; M is `sections.length` (every section listed
    // here has at least one item, the screen's `totals.sections`).
    items: z.number().int().nonnegative(),
    // Presentation order: section order, then item order.
    sections: z.array(actaChecklistSectionSchema),
})
    .strict();
/** Verdicts whose item materializes as an observation (ADR-0066) — the
 *  only ones that carry severity and description in the output. */
function isObservation(verdict) {
    return verdict === "con_obs" || verdict === "no_cumple";
}
/** Order then label — the GET /reports/:id/blocks and /items sort
 *  (order, then key), with the label standing in for the key because
 *  the output carries no keys. */
function byOrderThenLabel(aOrder, aLabel, bOrder, bLabel) {
    return aOrder - bOrder || aLabel.localeCompare(bLabel, "es");
}
/**
 * The acta's checklist outcome grouped by section, or null when the acta
 * has no checklist rows (the other verticals, and facility's standalone
 * hallazgo — ADR-0111 — which has no recorrido).
 */
export function buildActaChecklist(rows) {
    if (rows.length === 0)
        return null;
    const bySection = new Map();
    for (const row of rows) {
        const entry = bySection.get(row.sectionKey) ?? {
            label: row.sectionLabel,
            order: row.sectionOrder,
            rows: [],
        };
        entry.rows.push(row);
        bySection.set(row.sectionKey, entry);
    }
    const sections = [...bySection.values()]
        .sort((a, b) => byOrderThenLabel(a.order, a.label, b.order, b.label))
        .map((section) => {
        const sorted = [...section.rows].sort((a, b) => byOrderThenLabel(a.itemOrder, a.itemLabel, b.itemOrder, b.itemLabel));
        const counts = { cumple: 0, conObs: 0, noCumple: 0, nA: 0, total: sorted.length };
        const items = sorted.map((row) => {
            const verdict = row.supervisorVerdict;
            if (verdict === "cumple")
                counts.cumple += 1;
            else if (verdict === "con_obs")
                counts.conObs += 1;
            else if (verdict === "no_cumple")
                counts.noCumple += 1;
            else if (verdict === "n_a")
                counts.nA += 1;
            const observed = isObservation(verdict);
            return {
                label: row.itemLabel,
                verdict,
                severity: observed ? row.severity : null,
                description: observed ? row.description : null,
            };
        });
        return { label: section.label, counts, items };
    });
    return {
        items: sections.reduce((n, s) => n + s.items.length, 0),
        sections,
    };
}
/**
 * The "Estado general" band for a persisted reports.score (ADR-0026 —
 * the bucket mapping documented on computeReportScore). Never recomputes
 * the score: the closed-acta screen and the PDF both print the frozen
 * number, so they share this one mapping instead of two copies of the
 * thresholds.
 *
 *   5.00         → excelente
 *   4.00–4.99    → bueno
 *   3.00–3.99    → aceptable
 *   2.00–2.99    → bajo
 *   below 2.00   → crítico
 */
export function scoreBand(score) {
    if (score === null) {
        return { key: null, label: "Sin puntaje", scoreLabel: "—", filled: 0 };
    }
    let key;
    let label;
    if (score >= 5) {
        key = "excelente";
        label = "Excelente";
    }
    else if (score >= 4) {
        key = "bueno";
        label = "Bueno";
    }
    else if (score >= 3) {
        key = "aceptable";
        label = "Aceptable";
    }
    else if (score >= 2) {
        key = "bajo";
        label = "Bajo";
    }
    else {
        key = "critico";
        label = "Crítico";
    }
    return {
        key,
        label,
        scoreLabel: score.toFixed(1),
        filled: Math.max(0, Math.min(5, Math.round(score))),
    };
}
//# sourceMappingURL=acta-checklist.js.map